import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import { useNotify } from "react-admin";

import type {
  EventRelayAccessSyncPreviewResult,
  EventRelayAccessSyncPreviewRow,
  EventRelayAccessSyncPreviewStatus,
} from "@/containers/events/types";

import {
  fetchEventRelayAccessSyncPreview,
  syncEventRelayAccess,
} from "./event-relay-access-sync-api";

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  relayEventLabel?: string;
  canSync: boolean;
};

function defaultSelectedIds(rows: EventRelayAccessSyncPreviewRow[]): Set<string> {
  return new Set(rows.filter((row) => row.selectable && row.defaultSelected).map((row) => row.id));
}

function memberSourceLabel(source: string | null): string {
  switch (source) {
    case "family":
      return "Family";
    case "mehmaan":
      return "Mehmaan";
    case "legacy":
      return "Legacy";
    default:
      return "—";
  }
}

function statusChipColor(
  status: EventRelayAccessSyncPreviewStatus
): "success" | "warning" | "error" | "info" | "default" {
  switch (status) {
    case "create":
      return "success";
    case "update_reason":
      return "info";
    case "remove":
      return "warning";
    case "error":
      return "error";
    default:
      return "default";
  }
}

function SummaryChips({ summary }: { summary: EventRelayAccessSyncPreviewResult["summary"] }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      <Chip size="small" color="success" variant="outlined" label={`Grant: ${summary.create}`} />
      <Chip size="small" color="warning" variant="outlined" label={`Revoke: ${summary.remove}`} />
      <Chip size="small" variant="outlined" label={`Existing: ${summary.exists}`} />
      {summary.error > 0 ? (
        <Chip size="small" color="error" variant="outlined" label={`Errors: ${summary.error}`} />
      ) : null}
    </Stack>
  );
}

function PreviewRowCells({ row }: { row: EventRelayAccessSyncPreviewRow }) {
  return (
    <>
      <TableCell>{row.itsId}</TableCell>
      <TableCell>{row.memberName ?? "—"}</TableCell>
      <TableCell>{row.formNo ?? "—"}</TableCell>
      <TableCell>{row.markaz ?? "—"}</TableCell>
      <TableCell>{memberSourceLabel(row.memberSource)}</TableCell>
      <TableCell sx={{ maxWidth: 220 }}>
        <Typography variant="body2" noWrap title={row.relayReason ?? undefined}>
          {row.relayReason ?? "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={row.errorReason ?? ""} disableHoverListener={!row.errorReason}>
          <Chip
            size="small"
            color={statusChipColor(row.status)}
            label={row.statusLabel}
            variant={row.status === "exists" ? "outlined" : "filled"}
          />
        </Tooltip>
      </TableCell>
    </>
  );
}

export function EventRelayAccessSyncDialog({
  open,
  onClose,
  eventId,
  relayEventLabel,
  canSync,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const notify = useNotify();

  const [preview, setPreview] = useState<EventRelayAccessSyncPreviewResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyPreview = useCallback((next: EventRelayAccessSyncPreviewResult) => {
    setPreview(next);
    setSelectedIds(defaultSelectedIds(next.rows));
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEventRelayAccessSyncPreview(eventId);
      applyPreview(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load sync preview";
      setError(message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [applyPreview, eventId]);

  useEffect(() => {
    if (open) {
      void loadPreview();
    }
  }, [open, loadPreview]);

  const selectableRows = useMemo(
    () => preview?.rows.filter((row) => row.selectable) ?? [],
    [preview]
  );

  const allSelectableChecked =
    selectableRows.length > 0 && selectableRows.every((row) => selectedIds.has(row.id));

  const someSelectableChecked = selectableRows.some((row) => selectedIds.has(row.id));

  const toggleRow = (rowId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  const toggleAllSelectable = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(selectableRows.map((row) => row.id)));
      return;
    }
    setSelectedIds(new Set());
  };

  const selectedRows = useMemo(() => {
    if (!preview) return [];
    return preview.rows.filter(
      (row) => selectedIds.has(row.id) && row.selectable && row.syncAction
    );
  }, [preview, selectedIds]);

  const handleSync = async () => {
    if (!canSync || selectedRows.length === 0) {
      return;
    }

    setSyncing(true);
    setError(null);
    try {
      const payload = selectedRows.map((row) => ({
        itsId: row.itsId,
        action: row.syncAction as "create" | "revoke" | "update_reason",
      }));
      const result = await syncEventRelayAccess(eventId, payload);
      applyPreview(result.preview);

      const failed = result.results.filter((row) => !row.ok);
      const succeeded = result.results.filter((row) => row.ok);

      if (failed.length === 0) {
        notify(`Synced ${succeeded.length} relay access record(s)`, { type: "success" });
      } else if (succeeded.length === 0) {
        notify(failed[0]?.error ?? "Sync failed", { type: "error" });
      } else {
        notify(`Synced ${succeeded.length}; ${failed.length} failed`, { type: "warning" });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Sync failed";
      setError(message);
      notify(message, { type: "error" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      aria-labelledby="relay-access-sync-title"
    >
      <DialogTitle id="relay-access-sync-title" sx={{ pr: 6, pb: 1 }}>
        Sync relay access from niyaaz
        {relayEventLabel ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Relay event: {relayEventLabel}
          </Typography>
        ) : null}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: theme.spacing(1), top: theme.spacing(1) }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {preview ? (
          <Stack spacing={2}>
            <SummaryChips summary={preview.summary} />

            <Typography variant="body2" color="text.secondary">
              Compare niyaaz relay registrations with active relay access grants. Select rows to
              grant access for new registrations or revoke access that is no longer required.
            </Typography>

            {selectableRows.length > 0 ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelectableChecked}
                    indeterminate={someSelectableChecked && !allSelectableChecked}
                    onChange={(e) => toggleAllSelectable(e.target.checked)}
                  />
                }
                label="Select all eligible rows"
              />
            ) : null}

            {isMobile ? (
              <Stack spacing={1.5}>
                {preview.rows.map((row) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={(t) => ({
                      borderColor: selectedIds.has(row.id)
                        ? t.palette.primary.main
                        : t.palette.divider,
                      bgcolor: selectedIds.has(row.id)
                        ? alpha(t.palette.primary.main, 0.04)
                        : undefined,
                    })}
                  >
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          disabled={!row.selectable}
                          onChange={(e) => toggleRow(row.id, e.target.checked)}
                          sx={{ mt: -0.5, ml: -0.5 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={1}
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="subtitle2">
                              {row.memberName ?? row.itsId}
                            </Typography>
                            <Chip
                              size="small"
                              color={statusChipColor(row.status)}
                              label={row.statusLabel}
                              variant={row.status === "exists" ? "outlined" : "filled"}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            ITS {row.itsId}
                            {row.formNo != null ? ` · Form ${row.formNo}` : ""}
                            {row.markaz ? ` · ${row.markaz}` : ""}
                          </Typography>
                          {row.memberSource ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Source: {memberSourceLabel(row.memberSource)}
                            </Typography>
                          ) : null}
                          {row.relayReason ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Reason: {row.relayReason}
                            </Typography>
                          ) : null}
                          {row.errorReason ? (
                            <Typography
                              variant="caption"
                              color="error.main"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              {row.errorReason}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: "min(60vh, 520px)",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>ITS</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Form</TableCell>
                      <TableCell>Markaz</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover={row.selectable}
                        selected={selectedIds.has(row.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(row.id)}
                            disabled={!row.selectable}
                            onChange={(e) => toggleRow(row.id, e.target.checked)}
                          />
                        </TableCell>
                        <PreviewRowCells row={row} />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {preview.rows.length === 0 ? (
              <Alert severity="info">No relay registrations or access grants to compare.</Alert>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} disabled={syncing}>
          Close
        </Button>
        <Button onClick={() => void loadPreview()} disabled={loading || syncing}>
          Refresh
        </Button>
        {canSync ? (
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            disabled={loading || syncing || selectedRows.length === 0}
            onClick={() => void handleSync()}
          >
            {syncing ? "Syncing…" : `Sync selected (${selectedRows.length})`}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function EventRelayAccessSyncButton({
  eventId,
  relayEventId,
  relayEventLabel,
  canPreview,
  canSync,
}: {
  eventId: string;
  relayEventId: string;
  relayEventLabel?: string;
  canPreview: boolean;
  canSync: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (!canPreview || !relayEventId) {
    return null;
  }

  return (
    <>
      <Button variant="outlined" startIcon={<SyncIcon />} onClick={() => setOpen(true)}>
        Sync relay access
      </Button>
      <EventRelayAccessSyncDialog
        open={open}
        onClose={() => setOpen(false)}
        eventId={eventId}
        relayEventLabel={relayEventLabel}
        canSync={canSync}
      />
    </>
  );
}
